import api from "@/lib/api";

// Clip/reel extraction now runs as a background job on the server (ffmpeg
// against a remote Bunny/YouTube URL can easily outlive the platform's own
// front-door request timeout, especially for multi-clip reels) — the
// endpoints below just create the job and return its id immediately, so
// these helpers poll /download-jobs/{id} until it's done, then stream the
// finished file as a blob and trigger a normal browser "Save As" download.
const POLL_INTERVAL_MS = 2000;

function triggerBrowserDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

// The finished-file endpoint responds with a blob on success but a JSON
// error body on failure (e.g. expired result) — since axios is told to
// expect a blob either way, a failure has to be decoded back to text first.
async function fetchFinishedFile(jobId, fallbackFilename) {
  let res;
  try {
    res = await api.get(`/download-jobs/${jobId}/file`, { responseType: "blob" });
  } catch (err) {
    const data = err?.response?.data;
    if (data instanceof Blob) {
      try {
        const parsed = JSON.parse(await data.text());
        throw new Error(parsed.detail || "Could not download the file.");
      } catch {
        throw new Error("Could not download the file.");
      }
    }
    throw new Error(err?.response?.data?.detail || err?.message || "Could not download the file.");
  }
  const disposition = res.headers?.["content-disposition"] || "";
  const match = disposition.match(/filename="?([^";]+)"?/);
  triggerBrowserDownload(new Blob([res.data], { type: "video/mp4" }), match ? match[1] : fallbackFilename);
}

// Creates the job, polls it to completion, and downloads the result.
// `onStatus(job)` fires after every poll so callers can show progress
// ("Preparing your download…", "Extracting 2 of 5…"). Gives up after
// MAX_POLL_MS in case the job (or its server-side watchdog) ever gets
// stuck, rather than polling forever with the button stuck disabled.
const MAX_POLL_MS = 20 * 60 * 1000;

async function runDownloadJob(createJob, fallbackFilename, onStatus) {
  let job;
  try {
    const { data } = await createJob();
    job = data;
  } catch (err) {
    throw new Error(err?.response?.data?.detail || err?.message || "Could not start the download.");
  }
  onStatus?.(job);
  const deadline = Date.now() + MAX_POLL_MS;
  while (job.status === "queued" || job.status === "processing") {
    if (Date.now() > deadline) {
      throw new Error("This is taking much longer than expected — please try again in a bit.");
    }
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
    const { data } = await api.get(`/download-jobs/${job.id}`);
    job = data;
    onStatus?.(job);
  }
  if (job.status !== "complete") {
    throw new Error(job.error || "This download could not be completed.");
  }
  await fetchFinishedFile(job.id, job.result_filename || fallbackFilename);
}

// Downloads a short clip for one game event (goal/shot/save) — used from
// Game Detail's Events list, the highlights reel banner, and the Season
// Stats drill-down.
export async function downloadEventClip(teamId, gameId, eventId, onStatus) {
  await runDownloadJob(
    () => api.get(`/teams/${teamId}/games/${gameId}/events/${eventId}/clip`),
    "clip.mp4",
    onStatus,
  );
}

// Compiles the confirmed events of the given type(s) (e.g. ["goal","save"])
// into a single stitched-together file — used by the configurable
// Highlights Reel's "Download Highlight Reel" action.
export async function downloadHighlightsReel(teamId, gameId, types, onStatus) {
  await runDownloadJob(
    () => api.get(`/teams/${teamId}/games/${gameId}/highlights/download`, { params: { types: types.join(",") } }),
    "highlights.mp4",
    onStatus,
  );
}

// Cross-game "player highlights reel" (Season Stats page) — given a list of
// event ids that may span multiple games, the server extracts + normalizes
// + concatenates each one (see /highlights/player-reel/download).
export async function downloadPlayerReel(teamId, eventIds, onStatus) {
  await runDownloadJob(
    () => api.post(`/teams/${teamId}/highlights/player-reel/download`, { event_ids: eventIds }),
    "player-highlights.mp4",
    onStatus,
  );
}

// Short, button-friendly label for a download job's current status.
export function jobStatusLabel(job) {
  if (!job || job.status === "queued") return "Preparing your download…";
  if (job.status === "processing") {
    if (job.stage === "stitching") return "Stitching clips…";
    if (job.progress_total > 1) return `Extracting ${job.progress_current}/${job.progress_total}…`;
    return "Extracting clip…";
  }
  return "Preparing your download…";
}
